/**
 * Opportunity step — project fields on Opportunity (RHF + Zod; single column on all platforms).
 * React Hook Form + Zod; **single column on all platforms** (stacked fields; web matches mobile per #918).
 * Replaces the legacy create-opportunity step when the flag is on.
 *
 * **Figma (Visual Template `nzRrn94pPJjuXdhfsuRMN6`)** — PM Dev Mode pulls:
 * - `2-2` color foundations · `4054-3307` card anatomy/padding · `881-7557` Roboto type scale (+8 line-height)
 * · `7083-29970` responsive grid (24px fluid inset) · `1157-88137` selection/checkbox spacing (8px gaps)
 * Also: `8-831` fields, `64-2062` spacing, `2386-10683` alerts, `1735-2637` elevation, `2422-2112`, `2433-2366`.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useForm,
  useWatch,
  Controller,
  type FieldError,
  type Control,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormSetValue,
} from 'react-hook-form';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Pressable,
  type TextInput,
} from 'react-native';

import {
  FieldTextAssist,
  FORM_FIELD_TRAILING_ICON_SIZE,
  FormFieldTrailingIcon,
  FormInfoHelpModal,
  FormInput,
  FormDropdown,
  formFieldInStack,
  DealSelectionModal,
  StepForm,
  renderRequestFormRhfField,
  renderFormDataFields,
  type RequestFormRhfFieldContext,
  FormFieldShowInfoIconsProvider,
  useFormFieldShowInfoIcons,
  StepFirstSubsectionExpandProvider,
} from '../../../../components/forms';
import { SelectedFile } from '../../../../components/forms/FileUpload';
import {
  Battery,
  CloudOff,
  Droplet,
  Factory,
  Info,
  Layers,
  Link2,
  TextAssistPlaceholderIcon,
  Server,
  Snowflake,
  Zap,
} from '../../../../components/icons';
import { PermissionAwareField } from '../../../../components/PermissionAwareField';
import {
  StepShell,
  stepHeaderVariantForPlatform,
} from '../../../../components/StepShell';
import {
  OPPORTUNITY_PROJECT_DESCRIPTION_INFO,
  OPPORTUNITY_PROJECT_DESCRIPTION_PROMPT,
} from '../../../../constants/opportunityProjectDescriptionExample';
import { opportunityScopeInfoA11y } from '../../../../constants/opportunityScopeFieldInfo';
import { SECTION_IDS } from '../../../../constants/sectionIds';
import { useAuth } from '../../../../contexts/AuthContext';
import { useImpersonation } from '../../../../contexts/ImpersonationContext';
import { useButtonPermissions } from '../../../../hooks/useButtonPermissions';
import { useFormFieldVisibilityContext } from '../../../../hooks/useFormFieldVisibilityContext';
import { useFormPermissions } from '../../../../hooks/useFormPermissions';
import { useInsertExampleAtEnd } from '../../../../hooks/useInsertExampleAtEnd';
import { useOfflineToast } from '../../../../hooks/useOfflineToast';
import {
  OPPORTUNITY_NOTES_SUBSECTION_FIELDS,
  OPPORTUNITY_DEAL_REGISTRATION_SUBSECTION_FIELDS,
  OPPORTUNITY_PROJECT_SUMMARY_SUBSECTION_FIELDS,
  OPPORTUNITY_REQUESTOR_SUBSECTION_FIELDS,
  OPPORTUNITY_SCALE_TIMELINE_SUBSECTION_FIELDS,
} from '../../../../request/fields/requestFieldRegistry';
import { SavedAttachment } from '../../../../services/attachmentService';
import { fieldPermissionService } from '../../../../services/auth/fieldPermissionService';
import {
  colors,
  spacing,
  fontSize,
  lineHeight,
  fontWeight,
  getFieldRowStyleResponsive,
  getFieldColumnStyleResponsive,
} from '../../../../theme';
import { getVertivEmailVariants } from '../../../../utils/emailNormalization';
import {
  appendExampleTextRichHtml,
  exampleAlreadyInsertedRichHtml,
  fieldInfoPanelA11y,
} from '../../../../utils/fieldExampleInsert';
import {
  FormValidationError,
  getFieldError,
  PROJECT_DESCRIPTION_MAX_LENGTH,
} from '../../../../utils/formValidation';
import { subsectionHeaderMetricsProps } from '../../../../utils/subsectionHeaderMetrics';
import {
  OPPORTUNITY_PROJECT_SCOPE_OPTIONS,
  OPPORTUNITY_PROJECT_TYPE_OPTIONS,
  OPPORTUNITY_SMART_IT_NVL72_CONFIG_OPTIONS,
  OPPORTUNITY_SMART_IT_NVL8_CONFIG_OPTIONS,
  OPPORTUNITY_WORKLOAD_TYPE_OPTIONS,
  SMART_IT_CONFIG_GATES,
  WORKLOAD_TYPE_DISPLAY_LABELS,
  type SmartItConfigField,
  formatWorkloadTypeLabel,
  smartItConfigFieldsToClearOnWorkloadChange,
  oppDetailsRhfZodSchema,
  buildOpportunityRequestDetailsRhfValues,
  deriveOppIsIntegratedSolutionFromScope,
  OPPORTUNITY_ADDITIONAL_INFORMATION_MAX,
  type OpportunityRequestDetailsRhfValues,
} from '../../../../validation/opportunityProjectFieldsSchema';
import { buildOpportunityScopeAssessmentFieldSpecs } from '../../../fields/projections/opportunityFormSpecs';
import {
  opportunitySubsectionShouldExpand,
  OPPORTUNITY_DEAL_REGISTRATION_FIELD_KEYS,
  OPPORTUNITY_NOTES_FIELD_KEYS,
  OPPORTUNITY_PROJECT_SUMMARY_FIELD_KEYS,
  OPPORTUNITY_REQUESTOR_FIELD_KEYS,
  OPPORTUNITY_SCALE_TIMELINE_FIELD_KEYS,
} from '../opportunitySubsectionExpand';

import type { Deal } from '../../../../types';
import type { AppRole } from '../../../../types/auth';
import type { FieldInfoPanel } from '../../../../types/fieldInfoPanel';
import type { FC, ReactNode } from 'react';

const REGION_OPTION_SUBTITLES: Record<string, string> = {
  NAM: 'North America',
  EMEA: 'Europe, Middle East & Africa',
  APAC: 'Asia Pacific',
};

const PROJECT_TYPE_OPTIONS: readonly string[] =
  OPPORTUNITY_PROJECT_TYPE_OPTIONS;

const PROJECT_TYPE_SUBTITLES: Record<string, string> = {
  'Closet/Lab': 'Closet, lab, or small room deployments',
  'Data Center 10+ Racks': 'Traditional on-prem or colo, 10+ racks',
  Other: 'Custom or mixed',
};

const PROJECT_SCOPE_SUBTITLES: Record<string, string> = {
  'Facility Power': 'Utility, UPS, and distribution readiness',
  Rack: 'Cabinets and airflow',
  'Small UPS': 'Edge or closet UPS scope',
  Cooling: 'CRAC/CRAH and air-side readiness',
  'Liquid Cooling': 'CDU, manifolds, and fluid',
  'PreFab / Modular': 'Off-site or plug-in',
  Other: 'Mixed or TBD',
};

/** 16px icons for Project scope list rows (dropdown open list). */
/** "Is this an AI project or traditional compute?" (spec) */
// Workload options now come from the validation schema
// (OPPORTUNITY_WORKLOAD_TYPE_OPTIONS), which is the client SoT kept in sync with
// server/constants/opportunity.ts. The old local literal duplicated that list and
// still carried the retired 'Both' option.

/**
 * Option list per gated Smart IT config field.
 *
 * A Map (not an object literal) so the field names stay QUOTED string literals:
 * `opportunity-step-surface-parity` proves a registry step field is really rendered
 * by scanning this source for `'<dataField>'`, and prettier would strip quotes from
 * object keys. These fields render through a data-driven loop, so this is the only
 * place their names appear literally.
 */
const SMART_IT_CONFIG_OPTIONS = new Map<SmartItConfigField, readonly string[]>([
  ['smartItNvl72Config', OPPORTUNITY_SMART_IT_NVL72_CONFIG_OPTIONS],
  ['smartItNvl8Config', OPPORTUNITY_SMART_IT_NVL8_CONFIG_OPTIONS],
]);

const PROJECT_SCOPE_LIST_ICONS: Record<
  string,
  FC<{ size?: number; color?: string }>
> = {
  'Facility Power': Zap,
  Rack: Server,
  'Small UPS': Battery,
  Cooling: Snowflake,
  'Liquid Cooling': Droplet,
  'PreFab / Modular': Factory,
  Other: Layers,
};

function projectScopeListIcon(option: string): ReactNode {
  const Icon = PROJECT_SCOPE_LIST_ICONS[option];
  if (Icon == null) {
    return null;
  }
  return <Icon size={16} color={colors.textSecondary} />;
}

export type OpportunityProjectInfoPanel = FieldInfoPanel;

type OpportunityProjectInfoKey =
  | 'projectType'
  | 'projectScope'
  | 'siteAddress'
  | 'approximateKw'
  | 'projectDescription'
  | 'deploymentTimeline'
  | 'budget'
  | 'quoteNumber';

/** OP-UX-01 / OP-UX-02 — first card auto-expands via step provider; all cards remember expand state. */
const OPPORTUNITY_SUBSECTION_PERSIST = {
  persistSectionExpanded: true,
} as const;
const OPPORTUNITY_FIRST_SUBSECTION_EXPAND = {
  persistSectionExpanded: true,
} as const;

const INFO_PANELS: Record<
  OpportunityProjectInfoKey,
  OpportunityProjectInfoPanel
> = {
  projectType: {
    title: 'Project Type',
    bullets: [
      'Picks a category: closet/lab, larger data center (10+ racks), or other.',
      'Prefabricated or modular work belongs under Project scope (PreFab / Modular), not here.',
      'Routes the request to the right assessment playbooks and specialists.',
    ],
  },
  projectScope: {
    title: 'Project Scope',
    bullets: [
      'Select all workstreams that apply (facility power, rack, small UPS, cooling, liquid cooling, and more).',
      'Prefabricated or modular delivery: choose PreFab / Modular in this list (no separate Yes/No).',
      'Use Other when the scope is mixed or still being defined.',
      'Use this to set what the team will and will not assess.',
    ],
  },
  siteAddress: {
    title: 'Site address',
    bullets: [
      'Physical location for this site walk or assessment; may match the address captured on the opportunity step.',
    ],
  },
  approximateKw: {
    title: 'Approximate kW',
    bullets: [
      'Enter a number, a range, or TBD, whatever is closest to your situation.',
      'The field is optional. Leave it blank if load is unknown.',
    ],
  },
  projectDescription: OPPORTUNITY_PROJECT_DESCRIPTION_INFO,
  deploymentTimeline: {
    title: 'Project Deployment Timeline',
    bullets: [
      'Target quarter, fiscal year, or a rough when-you-need-answers date helps scheduling.',
      'Leave blank or put TBD if the schedule is still being negotiated.',
    ],
  },
  budget: {
    title: 'Budget (If Known)',
    bullets: [
      'A range or order-of-magnitude is enough for early planning.',
      'This field is optional. Skip it if budget is not set or is confidential for now.',
    ],
  },
  quoteNumber: {
    title: 'Quote Number',
    bullets: [
      'Enter the customer quote number or quote identifier when one is available.',
      'Leave blank if the quote has not been created yet.',
    ],
  },
} as const;

function infoPanelByKey(
  k: OpportunityProjectInfoKey,
): OpportunityProjectInfoPanel {
  switch (k) {
    case 'projectType':
      return INFO_PANELS.projectType;
    case 'projectScope':
      return INFO_PANELS.projectScope;
    case 'siteAddress':
      return INFO_PANELS.siteAddress;
    case 'approximateKw':
      return INFO_PANELS.approximateKw;
    case 'projectDescription':
      return INFO_PANELS.projectDescription;
    case 'deploymentTimeline':
      return INFO_PANELS.deploymentTimeline;
    case 'budget':
      return INFO_PANELS.budget;
    case 'quoteNumber':
      return INFO_PANELS.quoteNumber;
    default: {
      const _exhaustive: never = k;
      return _exhaustive;
    }
  }
}

const ICON_INFO = FORM_FIELD_TRAILING_ICON_SIZE;

function isDealLinkedInFormData(fd: Record<string, unknown>): boolean {
  return fd.dealLinked === 'true' || fd.dealLinked === true;
}

function opportunityProjectCustomerNameLabelIcon(
  fd: Record<string, unknown>,
): ReactNode {
  if (!isDealLinkedInFormData(fd)) {
    return null;
  }
  return <Link2 size={14} color={colors.primary} />;
}

function opportunityProjectCustomerNameDisabled(
  fd: Record<string, unknown>,
  baseDisabled: boolean,
): boolean {
  return baseDisabled || isDealLinkedInFormData(fd);
}

function mergeFieldError(
  rhf: FieldError | undefined,
  parentErrors: FormValidationError[],
  field: string,
): string | undefined {
  if (rhf && typeof rhf.message === 'string' && rhf.message.length > 0) {
    return rhf.message;
  }
  return getFieldError(parentErrors, field);
}

export type OpportunityFormFlushHandle = {
  getValuesForSave: () => OpportunityRequestDetailsRhfValues;
};

export interface ProjectInformationProps {
  formData: Record<string, unknown> & {
    stage?: string;
    sysRequestStage?: string;
  };
  updateFormData: (field: string, value: unknown) => void;
  validationErrors?: FormValidationError[];
  disabled?: boolean;
  requestId?: string;
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  isEditing?: boolean;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  /**
   * Create / Request Detail: bump when `formData` should re-seed RHF (request navigate or edit toggle).
   * Create flow: bump after #1114 draft hydrate so restored values appear in RHF controls.
   */
  parentSyncRevision?: number;
  /** Bump only after Save / submit validation fails — runs RHF `trigger()` for inline errors. */
  rhfValidationRevision?: number;
  /** Create Request: parent reads latest RHF values before save validation (autofill / sync gap). */
  registerOpportunityFormFlush?: (
    handle: OpportunityFormFlushHandle | null,
  ) => void;
  /** Request Detail opportunity: Basic/Advanced + PDF on the UOM row inside {@link ProjectInformationShell}. */
  headerUomRowEnd?: React.ReactNode;
  /**
   * Create / Request Detail: renders at the top of the **Project Information** card,
   * **above** the title row (via {@link SubsectionForm} `aboveTitle` on {@link StepForm}),
   * e.g. Project | Requestor subsection tabs.
   */
  projectInformationTopTabs?: React.ReactNode;
  /**
   * `'requestor'`: requestor card + Project Details + Scale/Timeline (no Project Summary).
   * `'project'`: Project Summary + those cards (no requestor card).
   */
  opportunityInformationSubTab?: 'project' | 'requestor';
  /** Injected requestor UI for {@link opportunityInformationSubTab} `'requestor'`. */
  opportunityProjectRequestorSlot?: React.ReactNode;
}

type OpportunityPdFormContextValue = {
  stepProps: ProjectInformationProps;
  control: Control<OpportunityRequestDetailsRhfValues>;
  rhfErrors: FieldErrors<OpportunityRequestDetailsRhfValues>;
  /** Needed by the Shell to clear a Smart IT config when its workload gate is de-selected. */
  setValue: UseFormSetValue<OpportunityRequestDetailsRhfValues>;
  getValues: UseFormGetValues<OpportunityRequestDetailsRhfValues>;
  /** Current workload picks — drives visibility of the gated Smart IT config fields. */
  workloadWatch: OpportunityRequestDetailsRhfValues['oppWorkloadType'];
  opportunityRequestDetailsRhfContext: RequestFormRhfFieldContext<OpportunityRequestDetailsRhfValues>;
  showDealModal: boolean;
  setShowDealModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleDealSelect: (deal: Deal) => Promise<void>;
  handleUnlinkDeal: () => void;
  useDefaultSponsorsOnForm: boolean;
  canShowLinkDeal: boolean;
  isOnline: boolean;
  isDisabled: boolean;
  requestStage: string;
  infoKey: OpportunityProjectInfoKey | null;
  setInfoKey: React.Dispatch<
    React.SetStateAction<OpportunityProjectInfoKey | null>
  >;
};

const OpportunityPdFormContext =
  React.createContext<OpportunityPdFormContextValue | null>(null);

function useOpportunityPdForm(): OpportunityPdFormContextValue {
  const ctx = React.useContext(OpportunityPdFormContext);
  if (ctx == null) {
    throw new Error(
      'Opportunity project information fields must render inside ProjectInformationFormProvider.',
    );
  }
  return ctx;
}

type FieldLabelWithInfoProps = {
  children: React.ReactNode;
  requiredMark?: boolean;
  onInfo: () => void;
  infoA11y: string;
  info: OpportunityProjectInfoPanel;
  /** Force (i) visibility. When omitted, follows FormFieldShowInfoIconsProvider. */
  showInfoIcon?: boolean;
  testID?: string;
};

/**
 * (i) opens the same `FormInfoHelpModal` on web and native (parent must render modal + `onInfo` → set key).
 */
function FieldLabelWithInfo({
  children,
  requiredMark,
  onInfo,
  infoA11y,
  info,
  showInfoIcon: showInfoIconProp,
  testID,
}: FieldLabelWithInfoProps) {
  const iconsFromContext = useFormFieldShowInfoIcons();
  const showIcons =
    showInfoIconProp !== undefined ? showInfoIconProp : iconsFromContext;
  const a11yLabel = fieldInfoPanelA11y(info);
  if (!showIcons) {
    return (
      <View style={styles.labelWithInfo}>
        <View style={styles.labelRow}>
          <Text style={styles.formFieldLabel}>
            {children}
            {requiredMark ? <Text style={styles.requiredMark}> *</Text> : null}
          </Text>
        </View>
      </View>
    );
  }
  return (
    <View style={styles.labelWithInfo}>
      <View style={styles.labelRow}>
        <Text style={styles.formFieldLabel}>
          {children}
          {requiredMark ? <Text style={styles.requiredMark}> *</Text> : null}
        </Text>
        <Pressable
          onPress={onInfo}
          style={({ pressed }) => [
            styles.infoButton,
            pressed && styles.infoButtonPressed,
          ]}
          accessibilityLabel={a11yLabel}
          accessibilityRole="button"
          accessibilityHint={infoA11y}
          hitSlop={8}
          testID={testID}
        >
          <Info size={ICON_INFO} color={colors.textSecondary} />
        </Pressable>
      </View>
    </View>
  );
}

export function ProjectInformationFormProvider({
  children,
  ...props
}: ProjectInformationProps & { children: React.ReactNode }) {
  const {
    formData,
    updateFormData,
    validationErrors = [],
    disabled = false,
    requestId,
    attachmentsBySection = {},
    onAttachFiles,
    onDeleteAttachment,
    isEditing = false,
    selectedFiles = {},
    onFilesSelected,
    offlineMode = false,
    parentSyncRevision,
    rhfValidationRevision,
    registerOpportunityFormFlush,
  } = props;
  const { canEditRequest } = useFormPermissions();
  const { isOnline } = useOfflineToast();
  const isDisabled = disabled || !canEditRequest;
  const [infoKey, setInfoKey] = useState<OpportunityProjectInfoKey | null>(
    null,
  );
  const requestStage = String(
    formData.sysRequestStage ?? formData.stage ?? 'DRAFT',
  );
  const { canShowLinkDeal } = useButtonPermissions(requestStage, requestId);
  const [showDealModal, setShowDealModal] = useState(false);

  const useDefaultSponsorsOnForm =
    formData.useDefaultSponsors === true ||
    formData.useDefaultSponsors === 'true';

  const zodSchema = useMemo(() => oppDetailsRhfZodSchema(), []);

  const {
    control,
    formState: { errors: rhfErrors },
    watch,
    setValue,
    reset,
    trigger,
    getValues,
  } = useForm<OpportunityRequestDetailsRhfValues>({
    resolver: zodResolver(zodSchema),
    defaultValues: buildOpportunityRequestDetailsRhfValues(formData),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const opportunityRequestDetailsRhfContext: RequestFormRhfFieldContext<OpportunityRequestDetailsRhfValues> =
    useMemo(
      () => ({
        control,
        formData: formData as Record<string, unknown>,
        requestId,
        requestStage,
        isDisabled,
        rhfErrors,
        validationErrors,
        mergeFieldError,
        updateFormData,
      }),
      [
        control,
        formData,
        requestId,
        requestStage,
        isDisabled,
        rhfErrors,
        validationErrors,
        updateFormData,
      ],
    );

  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const wasOfflineRef = useRef(!isOnline);

  const projectScopeWatch = watch('oppProjectScope');
  // Drives visibility of the gated Smart IT NVL config fields below.
  const workloadWatch = watch('oppWorkloadType');
  useEffect(() => {
    const derived = deriveOppIsIntegratedSolutionFromScope(projectScopeWatch);
    const current = String(formDataRef.current.oppIsIntegratedSolution ?? '');
    if (current !== derived) {
      updateFormData('oppIsIntegratedSolution', derived);
    }
  }, [projectScopeWatch, updateFormData]);

  useEffect(() => {
    if (wasOfflineRef.current && isOnline) {
      reset(buildOpportunityRequestDetailsRhfValues(formDataRef.current), {
        keepErrors: false,
        keepIsSubmitted: false,
      });
    }
    wasOfflineRef.current = !isOnline;
  }, [disabled, isOnline, reset]);

  useEffect(() => {
    if (parentSyncRevision === undefined) {
      return;
    }
    reset(buildOpportunityRequestDetailsRhfValues(formDataRef.current), {
      keepErrors: false,
      keepIsSubmitted: false,
    });
  }, [parentSyncRevision, reset]);

  useEffect(() => {
    if (rhfValidationRevision === undefined || rhfValidationRevision < 1) {
      return;
    }
    void trigger(undefined, { shouldFocus: Platform.OS !== 'web' });
  }, [rhfValidationRevision, trigger]);

  useEffect(() => {
    if (!registerOpportunityFormFlush) {
      return;
    }
    registerOpportunityFormFlush({
      getValuesForSave: () => getValues(),
    });
    return () => registerOpportunityFormFlush(null);
  }, [registerOpportunityFormFlush, getValues]);

  const handleUnlinkDeal = useCallback(() => {
    const autoAddedEmail = formData.autoAddedSponsorEmail as string | undefined;
    const currentSponsors: string[] = Array.isArray(formData.vertivSponsors)
      ? (formData.vertivSponsors as string[])
      : formData.vertivSponsors
      ? [String(formData.vertivSponsors)]
      : [];
    let nextSponsors = currentSponsors;
    if (!useDefaultSponsorsOnForm && autoAddedEmail) {
      const removeVariants = getVertivEmailVariants(autoAddedEmail);
      nextSponsors = currentSponsors.filter(
        email => !removeVariants.includes((email as string).toLowerCase()),
      );
      updateFormData('vertivSponsors', nextSponsors);
    }
    updateFormData('dealRegistrationNumber', '');
    updateFormData('oppOpportunityId', '');
    updateFormData('oppDealOpportunityId', '');
    updateFormData('dealLinked', 'false');
    updateFormData('autoAddedSponsorEmail', '');
    const merged: Record<string, unknown> = {
      ...formData,
      dealRegistrationNumber: '',
      oppOpportunityId: '',
      oppDealOpportunityId: '',
      dealLinked: 'false',
      autoAddedSponsorEmail: '',
      vertivSponsors: nextSponsors,
    };
    reset(buildOpportunityRequestDetailsRhfValues(merged), {
      keepErrors: false,
      keepIsSubmitted: false,
    });
  }, [formData, updateFormData, useDefaultSponsorsOnForm, reset]);

  const handleDealSelect = useCallback(
    async (deal: Deal) => {
      if (!deal?.dealRegistrationNumber || !deal.opportunityId) {
        setShowDealModal(false);
        return;
      }
      const next: Record<string, unknown> = { ...formData };
      next.dealRegistrationNumber = deal.dealRegistrationNumber;
      // #1475: store opp number even when the field is hidden for channel externals
      // (store-but-hide); display surfaces filter via field permissions.
      next.oppOpportunityId = deal.opportunityNumber || '';
      next.oppDealOpportunityId = deal.opportunityId;
      next.dealLinked = 'true';
      if (deal.endUserCompanyName) {
        next.customerName = deal.endUserCompanyName;
        updateFormData('customerName', deal.endUserCompanyName);
      }
      if (deal.opportunityName) {
        next.oppProjectName = deal.opportunityName;
        updateFormData('oppProjectName', deal.opportunityName);
      }
      updateFormData('dealRegistrationNumber', deal.dealRegistrationNumber);
      updateFormData('oppOpportunityId', deal.opportunityNumber || '');
      updateFormData('oppDealOpportunityId', deal.opportunityId);
      updateFormData('dealLinked', 'true');
      if (deal.vertivRepresentativeEmail && deal.sponsorValid === true) {
        next.autoAddedSponsorEmail = deal.vertivRepresentativeEmail;
        updateFormData('autoAddedSponsorEmail', deal.vertivRepresentativeEmail);
        if (useDefaultSponsorsOnForm) {
          next.useDefaultSponsors = false;
          next.vertivSponsors = [deal.vertivRepresentativeEmail];
          updateFormData('useDefaultSponsors', false);
          updateFormData('vertivSponsors', [deal.vertivRepresentativeEmail]);
        } else {
          const currentSponsors = Array.isArray(formData.vertivSponsors)
            ? (formData.vertivSponsors as string[])
            : formData.vertivSponsors
            ? [String(formData.vertivSponsors)]
            : [];
          const repVariants = getVertivEmailVariants(
            deal.vertivRepresentativeEmail,
          );
          const alreadyAdded = currentSponsors.some((email: string) =>
            repVariants.includes(email?.toLowerCase()),
          );
          if (!alreadyAdded) {
            next.vertivSponsors = [
              ...currentSponsors,
              deal.vertivRepresentativeEmail,
            ];
            updateFormData('vertivSponsors', [
              ...currentSponsors,
              deal.vertivRepresentativeEmail,
            ]);
          } else {
            next.vertivSponsors = currentSponsors;
          }
        }
      } else if (deal.vertivRepresentativeEmail && !deal.sponsorValid) {
        next.autoAddedSponsorEmail = '';
        updateFormData('autoAddedSponsorEmail', '');
      }
      reset(buildOpportunityRequestDetailsRhfValues(next), {
        keepErrors: false,
        keepIsSubmitted: false,
      });
      setShowDealModal(false);
    },
    [formData, updateFormData, useDefaultSponsorsOnForm, reset],
  );

  const contextValue = useMemo(
    (): OpportunityPdFormContextValue => ({
      stepProps: props,
      control,
      rhfErrors,
      setValue,
      getValues,
      workloadWatch,
      opportunityRequestDetailsRhfContext,
      showDealModal,
      setShowDealModal,
      handleDealSelect,
      handleUnlinkDeal,
      useDefaultSponsorsOnForm,
      canShowLinkDeal,
      isOnline,
      isDisabled,
      requestStage,
      infoKey,
      setInfoKey,
    }),
    [
      props,
      control,
      rhfErrors,
      setValue,
      getValues,
      workloadWatch,
      opportunityRequestDetailsRhfContext,
      showDealModal,
      handleDealSelect,
      handleUnlinkDeal,
      useDefaultSponsorsOnForm,
      canShowLinkDeal,
      isOnline,
      isDisabled,
      requestStage,
      infoKey,
    ],
  );

  return (
    <OpportunityPdFormContext.Provider value={contextValue}>
      {children}
    </OpportunityPdFormContext.Provider>
  );
}

export function ProjectInformationShell(): React.ReactElement {
  const {
    stepProps,
    control,
    rhfErrors,
    setValue,
    getValues,
    workloadWatch,
    opportunityRequestDetailsRhfContext,
    showDealModal,
    setShowDealModal,
    handleDealSelect,
    handleUnlinkDeal,
    useDefaultSponsorsOnForm,
    canShowLinkDeal,
    isOnline,
    isDisabled,
    requestStage,
    infoKey,
    setInfoKey,
  } = useOpportunityPdForm();
  const fieldRow = useMemo(
    () =>
      [
        getFieldRowStyleResponsive(false),
        styles.fieldRowTight,
        styles.pairedFieldRow,
      ] as const,
    [],
  );
  const colInRow: React.ComponentProps<typeof View>['style'] = useMemo(
    () => getFieldColumnStyleResponsive(true),
    [],
  );
  const {
    formData,
    updateFormData,
    validationErrors = [],
    disabled = false,
    requestId,
    attachmentsBySection = {},
    onAttachFiles,
    onDeleteAttachment,
    isEditing = false,
    selectedFiles = {},
    onFilesSelected,
    offlineMode = false,
  } = stepProps;
  const { user } = useAuth();
  const { isImpersonating, mode } = useImpersonation();
  const isReadOnlyImpersonation = isImpersonating && mode === 'read_only';
  const descriptionInputRef = useRef<TextInput>(null);
  const [assistOpen, setAssistOpen] = useState(false);
  const descriptionWriteAccess = fieldPermissionService.checkFieldAccessSync({
    userRoles: (user?.roles ?? []) as AppRole[],
    section: 'Opportunity Information',
    field: 'Description',
    requestStage,
    requestId,
    userEmail: user?.email,
  });
  const canWriteDescription = descriptionWriteAccess?.canWrite === true;
  const {
    insert: insertProjectDescriptionExample,
    selection: descriptionSelection,
    onSelectionChange: onDescriptionSelectionChange,
    focusAfterDismiss: focusDescriptionAfterAssist,
  } = useInsertExampleAtEnd({
    name: 'oppProjectDescription',
    maxLength: PROJECT_DESCRIPTION_MAX_LENGTH,
    getValues,
    setValue,
    updateFormData: (field, value) => updateFormData(field, value),
    inputRef: descriptionInputRef,
    onInserted: () => setAssistOpen(false),
    richHtml: true,
  });
  const showTextAssist =
    !isDisabled && canWriteDescription && !isReadOnlyImpersonation;
  const descriptionLive = useCallback((): string => {
    const raw = getValues('oppProjectDescription');
    return typeof raw === 'string' ? raw : '';
  }, [getValues]);
  useEffect(() => {
    if (!showTextAssist) {
      setAssistOpen(false);
    }
  }, [showTextAssist]);
  const projectSummaryFiles =
    selectedFiles[SECTION_IDS.OPPORTUNITY_PROJECT_SUMMARY] || [];
  const projectSummaryAttachments =
    attachmentsBySection[SECTION_IDS.OPPORTUNITY_PROJECT_SUMMARY] || [];
  const projectDetailsFiles =
    selectedFiles[SECTION_IDS.OPPORTUNITY_PROJECT_DETAILS] || [];
  const projectDetailsAttachments =
    attachmentsBySection[SECTION_IDS.OPPORTUNITY_PROJECT_DETAILS] || [];
  const scaleTimelineFiles =
    selectedFiles[SECTION_IDS.OPPORTUNITY_SCALE_TIMELINE] || [];
  const scaleTimelineAttachments =
    attachmentsBySection[SECTION_IDS.OPPORTUNITY_SCALE_TIMELINE] || [];
  const isRequestorTab =
    stepProps.opportunityInformationSubTab === 'requestor' &&
    stepProps.opportunityProjectRequestorSlot != null;
  const expandRequestor = opportunitySubsectionShouldExpand(
    OPPORTUNITY_REQUESTOR_FIELD_KEYS,
    validationErrors,
    rhfErrors,
  );
  const expandProjectSummary = opportunitySubsectionShouldExpand(
    OPPORTUNITY_PROJECT_SUMMARY_FIELD_KEYS,
    validationErrors,
    rhfErrors,
  );
  const expandDealRegistration = opportunitySubsectionShouldExpand(
    OPPORTUNITY_DEAL_REGISTRATION_FIELD_KEYS,
    validationErrors,
    rhfErrors,
  );
  const expandScaleTimeline = opportunitySubsectionShouldExpand(
    OPPORTUNITY_SCALE_TIMELINE_FIELD_KEYS,
    validationErrors,
    rhfErrors,
  );
  const watchedValues = useWatch({
    control,
  }) as OpportunityRequestDetailsRhfValues;
  const headerValues = useMemo(
    () => ({ ...formData, ...watchedValues } as Record<string, unknown>),
    [formData, watchedValues],
  );
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails: true,
    requestStage,
    requestId,
  });
  const requestorMetrics = useMemo(
    () =>
      subsectionHeaderMetricsProps(
        OPPORTUNITY_REQUESTOR_SUBSECTION_FIELDS,
        headerValues,
        fieldVisibility,
      ),
    [headerValues, fieldVisibility],
  );
  const projectSummaryMetrics = useMemo(
    () =>
      subsectionHeaderMetricsProps(
        OPPORTUNITY_PROJECT_SUMMARY_SUBSECTION_FIELDS,
        headerValues,
        fieldVisibility,
      ),
    [headerValues, fieldVisibility],
  );
  const dealRegistrationMetrics = useMemo(
    () =>
      subsectionHeaderMetricsProps(
        OPPORTUNITY_DEAL_REGISTRATION_SUBSECTION_FIELDS,
        headerValues,
        fieldVisibility,
      ),
    [headerValues, fieldVisibility],
  );
  const scaleTimelineMetrics = useMemo(
    () =>
      subsectionHeaderMetricsProps(
        OPPORTUNITY_SCALE_TIMELINE_SUBSECTION_FIELDS,
        headerValues,
        fieldVisibility,
      ),
    [headerValues, fieldVisibility],
  );
  const scopeAssessmentFieldSpecs = useMemo(
    () =>
      buildOpportunityScopeAssessmentFieldSpecs({
        preferredDateContainerStyle: formFieldInStack,
        siteAddressContainerStyle: formFieldInStack,
      }),
    [],
  );
  const scopeAssessmentFormCtx = useMemo(
    () => ({
      formData: headerValues,
      updateFormData,
      requestStage,
      requestId,
      disabled: isDisabled,
      help: {
        openKey: (key: string) => {
          if (key === 'oppSiteAddress') {
            setInfoKey('siteAddress');
          }
        },
        getA11y: (key: string) =>
          key === 'oppSiteAddress'
            ? opportunityScopeInfoA11y('oppSiteAddress')
            : { hint: '', description: '' },
      },
    }),
    [
      headerValues,
      updateFormData,
      requestStage,
      requestId,
      isDisabled,
      setInfoKey,
    ],
  );

  return (
    <StepShell
      title="Opportunity"
      testID="create-form-header-opportunity"
      headerVariant={stepHeaderVariantForPlatform()}
      
      uomRowEnd={stepProps.headerUomRowEnd}
      afterTitle={
        !isOnline ? (
          <View
            style={styles.offlineCallout}
            accessibilityRole="text"
            accessibilityLabel="You are offline. Your entries are kept on this device. Add attachments when back online; they will upload when you save."
          >
            <View style={styles.offlineCalloutRow}>
              <CloudOff
                size={20}
                color={colors.textSecondary}
                style={styles.offlineCalloutIcon}
              />
              <Text style={styles.offlineCalloutText}>
                You are offline. Your entries stay on this device. File
                attachments in this section are disabled until you are back
                online; you can add them after you reconnect and save.
              </Text>
            </View>
          </View>
        ) : null
      }
    >
      <FormFieldShowInfoIconsProvider showIcons={false}>
        <StepFirstSubsectionExpandProvider
          key={isRequestorTab ? 'requestor' : 'project'}
        >
          {/*
        Subsection card title: default `SubsectionForm` / `StepForm` uses brand orange (`colors.primary`).
        Neutral overrides are reserved for Site dimensions cards — see `SiteDimensionsStep`.
      */}
          {isRequestorTab ? (
            <StepForm
              title="Requestor"
              titleSubtitle="Who is requesting this assessment and how to reach them."
              {...requestorMetrics}
              aboveTitle={stepProps.projectInformationTopTabs}
              sectionId={SECTION_IDS.OPPORTUNITY_REQUESTOR}
              requestId={requestId}
              requestStage={requestStage}
              isEditing={isEditing || !disabled}
              disabled={disabled}
              isOffline={offlineMode}
              expandSection={expandRequestor}
              {...OPPORTUNITY_FIRST_SUBSECTION_EXPAND}
            >
              {stepProps.opportunityProjectRequestorSlot != null
                ? stepProps.opportunityProjectRequestorSlot
                : null}
            </StepForm>
          ) : null}
          {!isRequestorTab ? (
            <StepForm
              title="Project Summary"
              titleSubtitle="Region, customer, project name, workload, and description."
              {...projectSummaryMetrics}
              aboveTitle={stepProps.projectInformationTopTabs}
              sectionId={SECTION_IDS.OPPORTUNITY_PROJECT_SUMMARY}
              requestId={requestId}
              requestStage={requestStage}
              allowAttachments
              attachments={projectSummaryAttachments}
              onAttachFiles={() =>
                onAttachFiles?.(SECTION_IDS.OPPORTUNITY_PROJECT_SUMMARY)
              }
              onDeleteAttachment={onDeleteAttachment}
              isEditing={isEditing || !disabled}
              disabled={disabled}
              selectedFiles={projectSummaryFiles}
              onFilesSelected={
                onFilesSelected
                  ? files =>
                      onFilesSelected(
                        SECTION_IDS.OPPORTUNITY_PROJECT_SUMMARY,
                        files,
                      )
                  : undefined
              }
              isOffline={offlineMode}
              expandSection={expandProjectSummary}
              {...OPPORTUNITY_FIRST_SUBSECTION_EXPAND}
            >
              <>
                <View style={fieldRow}>
                  <View style={colInRow}>
                    {renderRequestFormRhfField<OpportunityRequestDetailsRhfValues>(
                      {
                        kind: 'rhfRegion',
                        name: 'region',
                        formSyncKey: 'region',
                        label: 'Region',
                        placeholder: 'Select region',
                        required: true,
                        testID: 'field-region',
                        permissionFieldId: 'region',
                        hideEmbeddedLabel: true,
                        optionSubtitles: REGION_OPTION_SUBTITLES,
                        allowClear: true,
                        triggerTextLines: 1,
                      },
                      opportunityRequestDetailsRhfContext,
                    )}
                  </View>
                </View>
                <View style={fieldRow}>
                  <View style={colInRow}>
                    {renderRequestFormRhfField<OpportunityRequestDetailsRhfValues>(
                      {
                        kind: 'rhfText',
                        name: 'customerName',
                        formSyncKey: 'customerName',
                        label: 'Customer name',
                        placeholder: 'Enter the customer or company name',
                        required: true,
                        testID: 'field-customer-name',
                        getLabelIcon: opportunityProjectCustomerNameLabelIcon,
                        isDisabledOverride:
                          opportunityProjectCustomerNameDisabled,
                      },
                      opportunityRequestDetailsRhfContext,
                    )}
                  </View>
                </View>

                <View style={colInRow}>
                  {renderRequestFormRhfField<OpportunityRequestDetailsRhfValues>(
                    {
                      kind: 'rhfText',
                      name: 'oppProjectName',
                      formSyncKey: 'oppProjectName',
                      permissionFieldId: 'oppProjectName',
                      label: 'Project name',
                      placeholder: 'Enter project name',
                    },
                    opportunityRequestDetailsRhfContext,
                  )}
                </View>

                <View style={fieldRow}>
                  <View style={colInRow}>
                    <PermissionAwareField
                      fieldId="oppWorkloadType"
                      requestStage={requestStage}
                      requestId={requestId}
                    >
                      <View>
                        <View style={styles.labelRow}>
                          <Text style={styles.formFieldLabel}>
                            Is this an AI project or traditional compute?
                          </Text>
                          <View
                            style={styles.labelRowIconSpacer}
                            importantForAccessibility="no"
                            pointerEvents="none"
                          />
                        </View>
                        <Controller
                          control={control}
                          name="oppWorkloadType"
                          render={({ field: { onChange, value } }) => {
                            const picks = Array.isArray(value)
                              ? (value as string[])
                              : [];
                            return (
                              <FormDropdown
                                label=""
                                multiple
                                value={picks}
                                options={[...OPPORTUNITY_WORKLOAD_TYPE_OPTIONS]}
                                optionLabels={WORKLOAD_TYPE_DISPLAY_LABELS}
                                onSelect={next => {
                                  if (!Array.isArray(next)) {
                                    return;
                                  }
                                  onChange(next);
                                  updateFormData('oppWorkloadType', next);
                                  // De-selecting a workload must clear its gated config —
                                  // otherwise the value is stranded: hidden in the UI but
                                  // live in the DB and on the PDF. Server twin:
                                  // smartItConfigWriteValue (orphan guard).
                                  for (const field of smartItConfigFieldsToClearOnWorkloadChange(
                                    next,
                                  )) {
                                    setValue(field, '', { shouldDirty: true });
                                    updateFormData(field, '');
                                  }
                                }}
                                placeholder="Select all that apply"
                                disabled={isDisabled}
                                readOnlyMode={isDisabled}
                                error={mergeFieldError(
                                  rhfErrors.oppWorkloadType as
                                    | FieldError
                                    | undefined,
                                  validationErrors,
                                  'oppWorkloadType',
                                )}
                                allowClear
                                triggerTextLines={1}
                                containerStyle={formFieldInStack}
                              />
                            );
                          }}
                        />
                      </View>
                    </PermissionAwareField>
                  </View>
                </View>
                {/* Smart IT NVL config fields — each shown only while its gating
                    workload option is selected; de-selecting clears the value above. */}
                {SMART_IT_CONFIG_GATES.map(({ field, gate }) => {
                  const picks = Array.isArray(workloadWatch)
                    ? (workloadWatch as string[])
                    : [];
                  if (!picks.includes(gate)) {
                    return null;
                  }
                  return (
                    <View style={fieldRow} key={field}>
                      <View style={colInRow}>
                        <PermissionAwareField
                          fieldId={field}
                          requestStage={requestStage}
                          requestId={requestId}
                        >
                          <View>
                            <View style={styles.labelRow}>
                              <Text style={styles.formFieldLabel}>
                                {formatWorkloadTypeLabel(gate)}
                              </Text>
                              <View
                                style={styles.labelRowIconSpacer}
                                importantForAccessibility="no"
                                pointerEvents="none"
                              />
                            </View>
                            <Controller
                              control={control}
                              name={field}
                              render={({ field: { onChange, value } }) => (
                                <FormDropdown
                                  label=""
                                  value={typeof value === 'string' ? value : ''}
                                  options={[
                                    ...(SMART_IT_CONFIG_OPTIONS.get(field) ??
                                      []),
                                  ]}
                                  onSelect={v => {
                                    onChange(v);
                                    updateFormData(field, v);
                                  }}
                                  placeholder="Select"
                                  disabled={isDisabled}
                                  readOnlyMode={isDisabled}
                                  error={mergeFieldError(
                                    rhfErrors[field],
                                    validationErrors,
                                    field,
                                  )}
                                  allowClear
                                  triggerTextLines={1}
                                  containerStyle={formFieldInStack}
                                />
                              )}
                            />
                          </View>
                        </PermissionAwareField>
                      </View>
                    </View>
                  );
                })}

                <PermissionAwareField
                  fieldId="oppProjectDescription"
                  requestStage={requestStage}
                  requestId={requestId}
                  containerStyle={styles.descBlock}
                >
                  <View style={styles.labelWithInfo}>
                    <View style={styles.labelRow}>
                      <Text style={styles.formFieldLabel}>
                        Project description
                      </Text>
                      {showTextAssist ? (
                        <FormFieldTrailingIcon
                          onPress={() => setAssistOpen(true)}
                          accessibilityLabel="Open examples for Project description"
                          accessibilityHint="Opens sample project descriptions you can insert or copy."
                          testID="opp-details-project-description-text-assist"
                          style={styles.textAssistTrigger}
                        >
                          <TextAssistPlaceholderIcon
                            size={FORM_FIELD_TRAILING_ICON_SIZE}
                            color={colors.textSecondary}
                          />
                        </FormFieldTrailingIcon>
                      ) : null}
                    </View>
                  </View>
                  <Controller
                    control={control}
                    name="oppProjectDescription"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <FormInput
                        ref={descriptionInputRef}
                        label="Project description"
                        value={value}
                        onBlur={onBlur}
                        onChangeText={v => {
                          onChange(v);
                          updateFormData('oppProjectDescription', v);
                          onDescriptionSelectionChange();
                        }}
                        onSelectionChange={onDescriptionSelectionChange}
                        selection={descriptionSelection}
                        placeholder={OPPORTUNITY_PROJECT_DESCRIPTION_PROMPT}
                        multiline
                        maxLength={PROJECT_DESCRIPTION_MAX_LENGTH}
                        disabled={isDisabled}
                        readOnlyMode={isDisabled}
                        hideLabel
                        testID="opp-details-project-description"
                        webMultilineAutoSize
                        webMultilineMinHeight={spacing.formTextAreaMinHeightWeb}
                        webMultilineMaxHeight={
                          spacing.formTextAreaMaxHeightWebTall
                        }
                        containerStyle={[
                          styles.projectDescriptionFormInputGroup,
                          formFieldInStack,
                        ]}
                        inputStyle={
                          Platform.OS === 'web'
                            ? undefined
                            : styles.projectDescriptionTextArea
                        }
                        richText
                        requestId={requestId}
                        requestSection={SECTION_IDS.OPPORTUNITY_PROJECT_SUMMARY}
                        isOnline={isOnline}
                        transportFieldKey="oppProjectDescription"
                        error={mergeFieldError(
                          rhfErrors.oppProjectDescription,
                          validationErrors,
                          'oppProjectDescription',
                        )}
                      />
                    )}
                  />
                </PermissionAwareField>
              </>
            </StepForm>
          ) : null}

          <StepForm
            title="Project Details"
            titleSubtitle="Deal registration, opportunity number, quote number, project type, scope, and site assessment."
            {...dealRegistrationMetrics}
            sectionId={SECTION_IDS.OPPORTUNITY_PROJECT_DETAILS}
            requestId={requestId}
            requestStage={requestStage}
            allowAttachments
            attachments={projectDetailsAttachments}
            onAttachFiles={() =>
              onAttachFiles?.(SECTION_IDS.OPPORTUNITY_PROJECT_DETAILS)
            }
            onDeleteAttachment={onDeleteAttachment}
            isEditing={isEditing || !disabled}
            disabled={disabled}
            readOnlyMode={disabled}
            selectedFiles={projectDetailsFiles}
            onFilesSelected={
              onFilesSelected
                ? files =>
                    onFilesSelected(
                      SECTION_IDS.OPPORTUNITY_PROJECT_DETAILS,
                      files,
                    )
                : undefined
            }
            isOffline={offlineMode}
            expandSection={expandDealRegistration}
            {...OPPORTUNITY_SUBSECTION_PERSIST}
          >
            <View style={styles.stackedDealLinkOpportunityColumn}>
              <View style={styles.stackedDealRegAndActionColumn}>
                <PermissionAwareField
                  fieldId="dealRegistrationNumber"
                  requestStage={requestStage}
                  requestId={requestId}
                >
                  <Controller
                    control={control}
                    name="dealRegistrationNumber"
                    render={({ field: { onChange, onBlur, value } }) => {
                      const dealLocked =
                        formData.dealLinked === 'true' ||
                        formData.dealLinked === true;
                      return (
                        <FormInput
                          label="Deal registration number"
                          value={value}
                          onBlur={onBlur}
                          onChangeText={v => {
                            onChange(v);
                            updateFormData('dealRegistrationNumber', v);
                          }}
                          placeholder="Enter the deal registration number (e.g., DR-2024-001234)"
                          disabled={isDisabled || dealLocked}
                          readOnlyMode={isDisabled}
                          labelIcon={
                            dealLocked ? (
                              <Link2 size={14} color={colors.primary} />
                            ) : undefined
                          }
                          containerStyle={formFieldInStack}
                          error={mergeFieldError(
                            rhfErrors.dealRegistrationNumber,
                            validationErrors,
                            'dealRegistrationNumber',
                          )}
                        />
                      );
                    }}
                  />
                </PermissionAwareField>
                {canShowLinkDeal && isOnline && !isDisabled ? (
                  <View style={styles.stackedLinkDealActionRow}>
                    {formData.dealLinked === 'true' ||
                    formData.dealLinked === true ? (
                      <TouchableOpacity
                        style={styles.unlinkDealTextButton}
                        onPress={handleUnlinkDeal}
                        accessibilityRole="button"
                        accessibilityLabel="Unlink deal"
                        testID="unlink-deal-button"
                      >
                        <Text style={styles.unlinkDealTextButtonText}>
                          Unlink deal
                        </Text>
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity
                        style={styles.linkDealButton}
                        onPress={() => setShowDealModal(true)}
                        accessibilityRole="button"
                        accessibilityLabel="Link deal"
                        testID="link-deal-button"
                      >
                        <Link2 size={14} color={colors.textLight} />
                        <Text style={styles.linkDealButtonText}>Link deal</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                ) : null}
              </View>
              <PermissionAwareField
                fieldId="oppOpportunityId"
                requestStage={requestStage}
                requestId={requestId}
              >
                <Controller
                  control={control}
                  name="oppOpportunityId"
                  render={({ field: { onChange, onBlur, value } }) => {
                    const dealLocked =
                      formData.dealLinked === 'true' ||
                      formData.dealLinked === true;
                    return (
                      <FormInput
                        label="Opportunity number"
                        value={value}
                        onBlur={onBlur}
                        onChangeText={v => {
                          onChange(v);
                          updateFormData('oppOpportunityId', v);
                        }}
                        placeholder="Enter the CRM opportunity number"
                        disabled={isDisabled || dealLocked}
                        readOnlyMode={isDisabled}
                        labelIcon={
                          dealLocked ? (
                            <Link2 size={14} color={colors.primary} />
                          ) : undefined
                        }
                        containerStyle={formFieldInStack}
                        error={mergeFieldError(
                          rhfErrors.oppOpportunityId,
                          validationErrors,
                          'oppOpportunityId',
                        )}
                      />
                    );
                  }}
                />
              </PermissionAwareField>
              <PermissionAwareField
                fieldId="oppQuoteNumber"
                requestStage={requestStage}
                requestId={requestId}
              >
                <View>
                  <FieldLabelWithInfo
                    onInfo={() => setInfoKey('quoteNumber')}
                    infoA11y="Quote Number information"
                    info={INFO_PANELS.quoteNumber}
                  >
                    Quote Number
                  </FieldLabelWithInfo>
                  <Controller
                    control={control}
                    name="oppQuoteNumber"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <FormInput
                        label="Quote Number"
                        value={value ?? ''}
                        onBlur={onBlur}
                        onChangeText={v => {
                          onChange(v);
                          updateFormData('oppQuoteNumber', v);
                        }}
                        placeholder="Enter quote number"
                        maxLength={255}
                        hideCharacterCount
                        disabled={isDisabled}
                        hideLabel
                        error={mergeFieldError(
                          rhfErrors.oppQuoteNumber,
                          validationErrors,
                          'oppQuoteNumber',
                        )}
                        containerStyle={formFieldInStack}
                      />
                    )}
                  />
                </View>
              </PermissionAwareField>
            </View>

            <View style={fieldRow}>
              <View style={colInRow}>
                <FieldLabelWithInfo
                  onInfo={() => setInfoKey('projectType')}
                  infoA11y="Project type information"
                  info={INFO_PANELS.projectType}
                >
                  Project type
                </FieldLabelWithInfo>
                <Controller
                  control={control}
                  name="oppProjectType"
                  render={({ field: { onChange, value } }) => (
                    <FormDropdown
                      label=""
                      value={value}
                      options={PROJECT_TYPE_OPTIONS}
                      onSelect={v => {
                        onChange(v);
                        updateFormData('oppProjectType', v);
                      }}
                      placeholder="Select project type"
                      disabled={isDisabled}
                      readOnlyMode={isDisabled}
                      error={mergeFieldError(
                        rhfErrors.oppProjectType,
                        validationErrors,
                        'oppProjectType',
                      )}
                      optionSubtitles={PROJECT_TYPE_SUBTITLES}
                      allowClear
                      triggerTextLines={1}
                      containerStyle={formFieldInStack}
                    />
                  )}
                />
              </View>
            </View>
            <View style={fieldRow}>
              <View style={colInRow}>
                <FieldLabelWithInfo
                  onInfo={() => setInfoKey('projectScope')}
                  infoA11y="Project scope information"
                  info={INFO_PANELS.projectScope}
                >
                  Project scope
                </FieldLabelWithInfo>
                <Controller
                  control={control}
                  name="oppProjectScope"
                  render={({ field: { onChange, value } }) => {
                    const arr = Array.isArray(value) ? (value as string[]) : [];
                    return (
                      <FormDropdown
                        label=""
                        multiple
                        value={arr}
                        options={[...OPPORTUNITY_PROJECT_SCOPE_OPTIONS]}
                        onSelect={next => {
                          if (!Array.isArray(next)) {
                            return;
                          }
                          onChange(next);
                          updateFormData('oppProjectScope', next);
                        }}
                        placeholder="Select all applicable scope items (Power, Cooling, Racks, etc.)"
                        disabled={isDisabled}
                        readOnlyMode={isDisabled}
                        error={mergeFieldError(
                          rhfErrors.oppProjectScope,
                          validationErrors,
                          'oppProjectScope',
                        )}
                        optionSubtitles={PROJECT_SCOPE_SUBTITLES}
                        renderOptionIcon={projectScopeListIcon}
                        allowClear
                        triggerTextLines={1}
                        containerStyle={formFieldInStack}
                      />
                    );
                  }}
                />
              </View>
            </View>

            {renderFormDataFields(
              scopeAssessmentFieldSpecs,
              scopeAssessmentFormCtx,
            )}
          </StepForm>

          <StepForm
            title="Feasibility"
            titleSubtitle="Deployment timeline, approximate kW, and budget when known."
            {...scaleTimelineMetrics}
            sectionId={SECTION_IDS.OPPORTUNITY_SCALE_TIMELINE}
            requestId={requestId}
            requestStage={requestStage}
            allowAttachments
            attachments={scaleTimelineAttachments}
            onAttachFiles={() =>
              onAttachFiles?.(SECTION_IDS.OPPORTUNITY_SCALE_TIMELINE)
            }
            onDeleteAttachment={onDeleteAttachment}
            isEditing={isEditing || !disabled}
            disabled={disabled}
            readOnlyMode={disabled}
            selectedFiles={scaleTimelineFiles}
            onFilesSelected={
              onFilesSelected
                ? files =>
                    onFilesSelected(
                      SECTION_IDS.OPPORTUNITY_SCALE_TIMELINE,
                      files,
                    )
                : undefined
            }
            isOffline={offlineMode}
            expandSection={expandScaleTimeline}
            {...OPPORTUNITY_SUBSECTION_PERSIST}
          >
            <View style={fieldRow}>
              <View style={colInRow}>
                <FieldLabelWithInfo
                  onInfo={() => setInfoKey('deploymentTimeline')}
                  infoA11y="Project deployment timeline information"
                  info={INFO_PANELS.deploymentTimeline}
                >
                  Project deployment timeline
                </FieldLabelWithInfo>
                <Controller
                  control={control}
                  name="oppDeploymentTimeline"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormInput
                      label="Project deployment timeline"
                      value={value}
                      onBlur={onBlur}
                      onChangeText={v => {
                        onChange(v);
                        updateFormData('oppDeploymentTimeline', v);
                      }}
                      placeholder="Enter expected deployment date or timeline (e.g., Q3 2026)"
                      disabled={isDisabled}
                      readOnlyMode={isDisabled}
                      hideLabel
                      error={mergeFieldError(
                        rhfErrors.oppDeploymentTimeline,
                        validationErrors,
                        'oppDeploymentTimeline',
                      )}
                      containerStyle={formFieldInStack}
                    />
                  )}
                />
              </View>
            </View>
            <View style={fieldRow}>
              <View style={colInRow}>
                <FieldLabelWithInfo
                  onInfo={() => setInfoKey('approximateKw')}
                  infoA11y="Approximate kW information"
                  info={INFO_PANELS.approximateKw}
                >
                  Approximate kW for project (if known)
                </FieldLabelWithInfo>
                <Controller
                  control={control}
                  name="oppApproximateKw"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormInput
                      label="Approximate kW for project (if known)"
                      value={value}
                      onBlur={onBlur}
                      onChangeText={v => {
                        onChange(v);
                        updateFormData('oppApproximateKw', v);
                      }}
                      placeholder="Enter estimated power requirement"
                      disabled={isDisabled}
                      readOnlyMode={isDisabled}
                      maxLength={200}
                      hideCharacterCount
                      hideLabel
                      error={mergeFieldError(
                        rhfErrors.oppApproximateKw,
                        validationErrors,
                        'oppApproximateKw',
                      )}
                      containerStyle={formFieldInStack}
                    />
                  )}
                />
              </View>
            </View>

            <View style={fieldRow}>
              <View style={colInRow}>
                <FieldLabelWithInfo
                  onInfo={() => setInfoKey('budget')}
                  infoA11y="Budget (if known) information"
                  info={INFO_PANELS.budget}
                >
                  Budget (if known)
                </FieldLabelWithInfo>
                <Controller
                  control={control}
                  name="oppBudget"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormInput
                      label="Budget (if known)"
                      value={value}
                      onBlur={onBlur}
                      onChangeText={v => {
                        onChange(v);
                        updateFormData('oppBudget', v);
                      }}
                      placeholder="Enter approximate budget range if known"
                      disabled={isDisabled}
                      readOnlyMode={isDisabled}
                      hideLabel
                      error={mergeFieldError(
                        rhfErrors.oppBudget,
                        validationErrors,
                        'oppBudget',
                      )}
                      containerStyle={formFieldInStack}
                    />
                  )}
                />
              </View>
            </View>
          </StepForm>
        </StepFirstSubsectionExpandProvider>
      </FormFieldShowInfoIconsProvider>

      {canShowLinkDeal && isOnline ? (
        <DealSelectionModal
          visible={showDealModal}
          onClose={() => setShowDealModal(false)}
          onSelect={handleDealSelect}
          requestId={requestId}
        />
      ) : null}

      <FormInfoHelpModal
        visible={infoKey != null}
        onClose={() => setInfoKey(null)}
        title={infoKey != null ? infoPanelByKey(infoKey).title : ''}
        bullets={infoKey != null ? [...infoPanelByKey(infoKey).bullets] : []}
      />
      <FieldTextAssist
        visible={assistOpen && showTextAssist}
        onClose={() => setAssistOpen(false)}
        onDismiss={focusDescriptionAfterAssist}
        title={OPPORTUNITY_PROJECT_DESCRIPTION_INFO.title}
        bullets={[...OPPORTUNITY_PROJECT_DESCRIPTION_INFO.bullets]}
        examplesIntro={OPPORTUNITY_PROJECT_DESCRIPTION_INFO.examplesIntro}
        examples={OPPORTUNITY_PROJECT_DESCRIPTION_INFO.examples ?? []}
        onInsertExample={example =>
          insertProjectDescriptionExample(example.text)
        }
        canInsertExample={text =>
          appendExampleTextRichHtml(
            descriptionLive(),
            text,
            PROJECT_DESCRIPTION_MAX_LENGTH,
          ).inserted
        }
        alreadyInserted={text =>
          exampleAlreadyInsertedRichHtml(descriptionLive(), text)
        }
        insertActionA11yLabel={example =>
          `Insert ${example.label} at the end of Project description`
        }
      />
    </StepShell>
  );
}

/** Additional Information card — last block on the Opportunity step (after team). */
export function ProjectInformationAdditionalSection(): React.ReactElement {
  const { stepProps, control, rhfErrors, isDisabled, requestStage, isOnline } =
    useOpportunityPdForm();
  const {
    updateFormData,
    validationErrors = [],
    disabled = false,
    requestId,
    attachmentsBySection = {},
    onAttachFiles,
    onDeleteAttachment,
    isEditing = false,
    selectedFiles = {},
    onFilesSelected,
    offlineMode = false,
  } = stepProps;
  const additionalSectionFiles =
    selectedFiles[SECTION_IDS.OPPORTUNITY_ADDITIONAL] || [];
  const additionalSectionAttachments =
    attachmentsBySection[SECTION_IDS.OPPORTUNITY_ADDITIONAL] || [];
  const expandNotes = opportunitySubsectionShouldExpand(
    OPPORTUNITY_NOTES_FIELD_KEYS,
    validationErrors,
    rhfErrors,
  );
  const watchedValues = useWatch({
    control,
  }) as OpportunityRequestDetailsRhfValues;
  const headerValues = useMemo(
    () =>
      ({ ...stepProps.formData, ...watchedValues } as Record<string, unknown>),
    [stepProps.formData, watchedValues],
  );
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails: true,
    requestStage,
    requestId,
  });
  const notesMetrics = useMemo(
    () =>
      subsectionHeaderMetricsProps(
        OPPORTUNITY_NOTES_SUBSECTION_FIELDS,
        headerValues,
        fieldVisibility,
      ),
    [headerValues, fieldVisibility],
  );

  return (
    <FormFieldShowInfoIconsProvider showIcons={false}>
      <StepForm
        title="Additional Information"
        titleSubtitle="Additional opportunity notes and supporting documents."
        {...notesMetrics}
        sectionId={SECTION_IDS.OPPORTUNITY_ADDITIONAL}
        requestId={requestId}
        requestStage={requestStage}
        allowAttachments
        attachments={additionalSectionAttachments}
        onAttachFiles={() =>
          onAttachFiles?.(SECTION_IDS.OPPORTUNITY_ADDITIONAL)
        }
        onDeleteAttachment={onDeleteAttachment}
        isEditing={isEditing || !disabled}
        disabled={disabled}
        readOnlyMode={disabled}
        selectedFiles={additionalSectionFiles}
        onFilesSelected={
          onFilesSelected
            ? files =>
                onFilesSelected(SECTION_IDS.OPPORTUNITY_ADDITIONAL, files)
            : undefined
        }
        isOffline={offlineMode}
        expandSection={expandNotes}
        {...OPPORTUNITY_SUBSECTION_PERSIST}
      >
        <View style={styles.descBlock}>
          <PermissionAwareField
            fieldId="siteAdditionalSiteInformation"
            requestStage={requestStage}
            requestId={requestId}
          >
            <Controller
              control={control}
              name="siteAdditionalSiteInformation"
              render={({ field: { onChange, onBlur, value } }) => (
                <FormInput
                  label="Additional Opportunity Information"
                  value={value}
                  onBlur={onBlur}
                  onChangeText={v => {
                    onChange(v);
                    updateFormData('siteAdditionalSiteInformation', v);
                  }}
                  placeholder="Enter any additional notes or comments"
                  multiline
                  maxLength={OPPORTUNITY_ADDITIONAL_INFORMATION_MAX}
                  disabled={isDisabled}
                  readOnlyMode={isDisabled}
                  testID="opp-details-additional-opportunity-info"
                  webMultilineAutoSize
                  webMultilineMinHeight={spacing.formTextAreaMinHeightWeb}
                  webMultilineMaxHeight={spacing.formTextAreaMaxHeightWebTall}
                  containerStyle={[
                    styles.projectDescriptionFormInputGroup,
                    formFieldInStack,
                  ]}
                  inputStyle={
                    Platform.OS === 'web'
                      ? undefined
                      : styles.projectDescriptionTextArea
                  }
                  richText
                  requestId={requestId}
                  requestSection={SECTION_IDS.OPPORTUNITY_ADDITIONAL}
                  isOnline={isOnline}
                  transportFieldKey="siteAdditionalSiteInformation"
                  error={mergeFieldError(
                    rhfErrors.siteAdditionalSiteInformation,
                    validationErrors,
                    'siteAdditionalSiteInformation',
                  )}
                />
              )}
            />
          </PermissionAwareField>
        </View>
      </StepForm>
    </FormFieldShowInfoIconsProvider>
  );
}

export const ProjectInformation: React.FC<ProjectInformationProps> = props => (
  <ProjectInformationFormProvider {...props}>
    <ProjectInformationShell />
    <ProjectInformationAdditionalSection />
  </ProjectInformationFormProvider>
);

const styles = StyleSheet.create({
  offlineCallout: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: spacing.cardBorderRadius,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderLeftWidth: spacing.alertInfoLeftBorder,
    borderLeftColor: colors.textSecondary,
    paddingHorizontal: spacing.cardPadding,
    paddingVertical: spacing.cardPadding,
    marginBottom: spacing.pageTitleMarginBottom,
  },
  offlineCalloutRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  offlineCalloutIcon: {
    marginRight: spacing.subsectionTitleToFields,
    marginTop: 2,
  },
  offlineCalloutText: {
    flex: 1,
    fontSize: fontSize.body,
    lineHeight: lineHeight.body,
    color: colors.textSecondary,
  },
  /** Native: fixed multiline block. Web: `webMultilineAutoSize` (Figma: no drag resize) + height tokens. */
  projectDescriptionTextArea: {
    minHeight: spacing.formTextAreaMinHeightNative,
    height: spacing.formTextAreaMinHeightNative,
  },
  /**
   * Stacked `fieldRow`: zero row margin; spacing between field groups is `formFieldGroupGap` on
   * `FormInput` / `FormDropdown` only.
   */
  fieldRowTight: {
    marginBottom: 0,
  },
  /** Row of paired inputs: always full width of the card so nested columns can’t overflow on narrow viewports. */
  pairedFieldRow: {
    width: '100%' as const,
    minWidth: 0,
    alignSelf: 'stretch' as const,
  },
  /**
   * Stacked: deal registration + link action, then opportunity number, then quote
   * number — one `formFieldGroupGap` rhythm between groups.
   */
  stackedDealLinkOpportunityColumn: {
    width: '100%' as const,
    alignSelf: 'stretch' as const,
    flexDirection: 'column' as const,
    gap: spacing.formFieldGroupGap,
  },
  stackedDealRegAndActionColumn: {
    width: '100%' as const,
    flexDirection: 'column' as const,
    gap: spacing.formFieldStackedActionGap,
  },
  stackedLinkDealActionRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    alignSelf: 'flex-start' as const,
  },
  /** Figma: field labels — 14/500, primary, consistent line height with info icons. */
  formFieldLabel: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  labelWithInfo: {
    width: '100%',
    ...Platform.select({
      web: { zIndex: 1, overflow: 'visible' },
      default: {},
    }),
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.fieldVerticalGap,
    ...Platform.select({
      web: { overflow: 'visible' },
      default: {},
    }),
  },
  // Trailing space matching `FieldLabelWithInfo` info button so a paired label row is same height.
  labelRowIconSpacer: {
    marginLeft: spacing.infoIconPadding,
    width: ICON_INFO + 2 * spacing.infoIconPadding,
    height: ICON_INFO + 2 * spacing.infoIconPadding,
  },
  requiredMark: { color: colors.error },
  infoButton: {
    marginLeft: spacing.infoIconPadding,
    padding: spacing.infoIconPadding,
  },
  infoButtonPressed: { opacity: 0.65 },
  textAssistTrigger: {
    marginLeft: spacing.infoIconPadding,
  },
  /** Same vertical rhythm as other full-width field groups (no extra top margin). */
  /** Full width, left-aligned with the rest of the form (no isolated center block). */
  descBlock: {
    marginTop: 0,
    ...Platform.select({
      web: {
        width: '100%' as const,
        alignSelf: 'stretch' as const,
      },
      default: {},
    }),
  },
  projectDescriptionFormInputGroup: {
    marginBottom: 0,
  },
  // Align the action to the 48px input, not the label: skip label+gap (same as FormInput layout).
  linkDealContainer: {
    paddingTop: spacing.formInputLabelToControlOffset,
    minHeight:
      spacing.formInputLabelToControlOffset + spacing.formControlHeight,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  linkDealButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingVertical: spacing.formCompactActionPaddingVertical,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    borderRadius: spacing.cardBorderRadius,
  },
  linkDealButtonText: {
    color: colors.textLight,
    fontWeight: fontWeight.semibold,
    fontSize: fontSize.body,
  },
  unlinkDealTextButton: {
    paddingVertical: spacing.formCompactActionPaddingVertical,
    paddingHorizontal: spacing.xs,
    alignSelf: 'flex-start',
  },
  unlinkDealTextButtonText: {
    color: colors.primary,
    fontWeight: fontWeight.semibold,
    fontSize: fontSize.body,
  },
});
